<script setup lang="ts">
  import { ref, watch } from "vue";
  import { Map, Layers, Sources, Geometries, Styles, Interactions } from "vue3-openlayers";

  import type { ObjectEvent } from "ol/Object";
  import { fromLonLat, toLonLat } from "ol/proj";
  import type { View } from "ol";
  import type { Select } from "ol/interaction";
  import { click } from "ol/events/condition";
  import { extend, createEmpty } from "ol/extent";
  import type { TPickupPoint } from "../types";

  import hereIcon from "../assets/here.svg";

  // Props
  interface Props {
    allPickupPoints: TPickupPoint[];
    selectedPickupPoint?: TPickupPoint | null;
    isSearchResult?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    allPickupPoints: () => [],
    selectedPickupPoint: null,
    isSearchResult: false,
  });

  // Emits
  const emit = defineEmits<{
    "pickup-point-selected": [pickupPoint: TPickupPoint | null];
    "pickup-points-in-view-changed": [pickupPoints: TPickupPoint[]];
    "bounding-box-changed": [
      boundingBox: {
        north: number;
        south: number;
        east: number;
        west: number;
      },
    ];
  }>();

  const map = ref(null);
  const projection = ref("EPSG:3857");
  const view = ref<View>();
  const center = ref(fromLonLat([14.4378, 50.0755])); // Prague coordinates in EPSG:4326 converted to EPSG:3857
  const position = ref([]);
  const selectInteraction = ref<Select | null>(null);
  const selectedPickupPoint = ref<TPickupPoint | null>(null);
  const pickupPointsInView = ref<TPickupPoint[]>([]);

  const geoLocChange = (event: ObjectEvent) => {
    position.value = event.target.getPosition();
    view.value?.setCenter(event.target?.getPosition());
  };

  // Function to calculate pickup points currently in view
  const calculatePickupPointsInView = () => {
    if (!view.value) return;

    const extent = view.value.calculateExtent();
    const pointsInView: TPickupPoint[] = [];

    props.allPickupPoints.forEach((point) => {
      const pointCoords = fromLonLat([point.lon, point.lat]);
      // Check if point coordinates are within the current view extent
      if (
        pointCoords[0] >= extent[0] &&
        pointCoords[0] <= extent[2] &&
        pointCoords[1] >= extent[1] &&
        pointCoords[1] <= extent[3]
      ) {
        pointsInView.push(point);
      }
    });

    // Only emit if the points in view have actually changed
    const currentIds = pointsInView.map((p) => p.id).sort();
    const previousIds = pickupPointsInView.value.map((p) => p.id).sort();

    if (JSON.stringify(currentIds) !== JSON.stringify(previousIds)) {
      pickupPointsInView.value = pointsInView;
      emit("pickup-points-in-view-changed", pointsInView);
    }
  };

  // Function to calculate and emit the current bounding box
  const calculateAndEmitBoundingBox = () => {
    if (!view.value) return;

    const extent = view.value.calculateExtent();

    // Convert extent coordinates from map projection to lat/lon
    const bottomLeft = toLonLat([extent[0], extent[1]]);
    const topRight = toLonLat([extent[2], extent[3]]);

    const boundingBox = {
      west: bottomLeft[0], // min longitude
      south: bottomLeft[1], // min latitude
      east: topRight[0], // max longitude
      north: topRight[1], // max latitude
    };

    emit("bounding-box-changed", boundingBox);
  };

  // Handle map view changes - called from template events
  const onMapMoveEnd = () => {
    calculatePickupPointsInView();
    calculateAndEmitBoundingBox();
  };

  // Setup view event listeners when view becomes available
  const setupViewListeners = () => {
    if (view.value) {
      // Initial calculation
      calculatePickupPointsInView();
      calculateAndEmitBoundingBox();
    }
  };

  // Function to fit all pickup points in the view
  const fitAllPickupPoints = () => {
    if (!view.value || props.allPickupPoints.length === 0) return;

    // Calculate extent for all pickup points
    const combinedExtent = createEmpty();
    props.allPickupPoints.forEach((point) => {
      const pointCoords = fromLonLat([point.lon, point.lat]);
      extend(combinedExtent, [pointCoords[0], pointCoords[1], pointCoords[0], pointCoords[1]]);
    });

    // Fit view to show all points with some padding
    view.value.fit(combinedExtent, {
      padding: [50, 50, 50, 50],
      duration: 1000,
      maxZoom: 15,
    });
  };

  // Only watch for allPickupPoints changes (when new data is loaded)
  watch(
    () => props.allPickupPoints,
    (newPoints, oldPoints) => {
      calculatePickupPointsInView();
    },
  );

  // Watch for search results specifically to trigger zoom-to-fit
  watch(
    () => props.isSearchResult,
    (isSearchResult) => {
      if (isSearchResult && props.allPickupPoints.length > 0) {
        // Small delay to ensure the map is ready
        setTimeout(() => {
          fitAllPickupPoints();
        }, 100);
      }
    },
  );

  // Watch for selectedPickupPoint changes to zoom to specific pickup point
  watch(
    () => props.selectedPickupPoint,
    (newSelectedPoint, oldSelectedPoint) => {
      if (newSelectedPoint && newSelectedPoint !== oldSelectedPoint && view.value) {
        // Zoom to the selected pickup point
        const pointCoords = fromLonLat([newSelectedPoint.lon, newSelectedPoint.lat]);
        view.value.animate({
          center: pointCoords,
          zoom: 18,
          duration: 1000,
        });

        // Update local selected state
        selectedPickupPoint.value = newSelectedPoint;
      }
    },
  );

  const overrideStyleFunction = (feature, style, resolution) => {
    const clusteredFeatures = feature.get("features");
    const size = clusteredFeatures.length;
    if (size > 5) {
      style.getText().setText("5+");
    } else {
      style.getText().setText(size.toString());
    }
    return style;
  };

  const featureSelected = (event) => {
    // Check if we have selected features
    if (event.selected && event.selected.length > 0) {
      const selectedFeature = event.selected[0];
      const clusteredFeatures = selectedFeature.get("features");

      if (clusteredFeatures && clusteredFeatures.length > 1) {
        // Calculate combined extent using OpenLayers extent functions
        const combinedExtent = createEmpty();
        clusteredFeatures.forEach((feature) => {
          extend(combinedExtent, feature.getGeometry().getExtent());
        });

        // Zoom to the extent with some padding
        view.value?.fit(combinedExtent, {
          padding: [50, 50, 50, 50],
          duration: 1000,
          maxZoom: 18,
        });

        // Deselect the cluster after zooming
        setTimeout(() => {
          if (selectInteraction.value) {
            selectInteraction.value.getFeatures?.().clear();
          }
        }, 100);

        // Clear selected pickup point when zooming to cluster
        selectedPickupPoint.value = null;
        emit("pickup-point-selected", null);
      } else if (clusteredFeatures && clusteredFeatures.length === 1) {
        // Single feature - handle individual pickup point selection
        const pickupPointData = clusteredFeatures[0].get("pickupPointData");
        selectedPickupPoint.value = pickupPointData;
        emit("pickup-point-selected", pickupPointData);
        console.log("Individual pickup point selected:", pickupPointData);
      }
    } else {
      // No features selected - clear selection
      selectedPickupPoint.value = null;
      emit("pickup-point-selected", null);
    }
  };
</script>

<template>
  <Map.OlMap
    style="min-width: 400px; height: 100%"
    ref="map"
    :loadTilesWhileAnimating="true"
    :loadTilesWhileInteracting="true"
    @moveend="onMapMoveEnd"
    @zoomend="onMapMoveEnd"
  >
    <Map.OlView
      :center="center"
      :zoom="15"
      :projection="projection"
      ref="view"
      @postrender="setupViewListeners"
    />
    <Layers.OlTileLayer>
      <Sources.OlSourceOsm />
    </Layers.OlTileLayer>

    <Layers.OlVectorLayer>
      <Sources.OlSourceCluster :distance="80">
        <Sources.OlSourceVector>
          <Map.OlFeature
            v-for="point in allPickupPoints"
            :key="point.id"
            :properties="{ pickupPointData: point }"
          >
            <Geometries.OlGeomPoint
              :coordinates="fromLonLat([point.lon, point.lat])"
            ></Geometries.OlGeomPoint>
          </Map.OlFeature>
        </Sources.OlSourceVector>
      </Sources.OlSourceCluster>

      <Styles.OlStyle :override-style-function="overrideStyleFunction">
        <Styles.OlStyleStroke color="red" :width="2"></Styles.OlStyleStroke>
        <Styles.OlStyleFill color="rgba(255,255,255,0.1)"></Styles.OlStyleFill>
        <Styles.OlStyleCircle :radius="20">
          <Styles.OlStyleFill color="#3399CC"></Styles.OlStyleFill>
          <Styles.OlStyleStroke color="#fff" :width="1"></Styles.OlStyleStroke>
        </Styles.OlStyleCircle>

        <Styles.OlStyleText>
          <Styles.OlStyleFill color="#fff"></Styles.OlStyleFill>
        </Styles.OlStyleText>
      </Styles.OlStyle>
    </Layers.OlVectorLayer>

    <Interactions.OlInteractionSelect
      :condition="click"
      @select="featureSelected"
      ref="selectInteraction"
    >
      <Styles.OlStyle :override-style-function="overrideStyleFunction">
        <Styles.OlStyleStroke color="red" :width="2"></Styles.OlStyleStroke>
        <Styles.OlStyleFill color="rgba(255,255,255,0.1)"></Styles.OlStyleFill>
        <Styles.OlStyleCircle :radius="20">
          <Styles.OlStyleFill color="#3399CC"></Styles.OlStyleFill>
          <Styles.OlStyleStroke color="#f00" :width="1"></Styles.OlStyleStroke>
        </Styles.OlStyleCircle>

        <Styles.OlStyleText>
          <Styles.OlStyleFill color="#fff"></Styles.OlStyleFill>
        </Styles.OlStyleText>
      </Styles.OlStyle>
    </Interactions.OlInteractionSelect>

    <Map.OlGeolocation :projection="projection" @change:position="geoLocChange">
      <template>
        <Layers.OlVectorLayer :zIndex="2">
          <Sources.OlSourceVector>
            <Map.OlFeature ref="positionFeature">
              <Geometries.OlGeomPoint
                ol-geom-point
                :coordinates="position"
              ></Geometries.OlGeomPoint>
              <Styles.OlStyle>
                <Styles.OlStyleIcon :src="hereIcon" :scale="0.5"></Styles.OlStyleIcon>
              </Styles.OlStyle>
            </Map.OlFeature>
          </Sources.OlSourceVector>
        </Layers.OlVectorLayer>
      </template>
    </Map.OlGeolocation>
  </Map.OlMap>
</template>

<style scoped lang="scss">
  #balikobot-pickup-modal {
    .bb-pickup-point-selector {
      .map-container {
        .ol-map {
          background-color: #ddd !important;
        }
      }
    }
  }
</style>
